import { Lock } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { resetPassword } from '@/domains/auth/apis/auth';
import { usePendingAuthStore } from '@/domains/auth/store/pendingAuthStore';
import { apiErrorBody, apiErrorMessage } from '@/domains/auth/utils/apiError';
import { FORGOT_PASSWORD_ROUTE } from '@/domains/auth/utils/authRoutes';
import { passwordError } from '@/domains/auth/utils/validation';
import PasswordMobile from '@/domains/mobile-web/components/PasswordMobile/PasswordMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { LOGIN_ROUTE } from '@/shared/utils/loginRedirect';
import { showToast } from '@/shared/utils/showToast';

interface FieldErrors {
  password?: string;
  confirm_password?: string;
}

// Last step of Lupa Password: the server session holds the phone number verified on the previous screen.
const NewPassword = () => {
  const navigate = useNavigate();
  const resetVerified = usePendingAuthStore((state) => state.resetVerified);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  // Set once saved, so clearing the pending state does not send the user back to Lupa Password
  const finished = useRef(false);

  if (finished.current) return null;
  if (!resetVerified) {
    return <Navigate to={FORGOT_PASSWORD_ROUTE} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: FieldErrors = {
      password: passwordError(password),
      confirm_password:
        confirmPassword !== password ? 'Konfirmasi password tidak sesuai.' : undefined,
    };
    setErrors(nextErrors);
    if (nextErrors.password || nextErrors.confirm_password) return;

    setIsSaving(true);
    try {
      await resetPassword({ password, confirm_password: confirmPassword });
      finished.current = true;
      usePendingAuthStore.getState().clear();
      showToast({
        type: 'success',
        title: 'Password diperbarui',
        message: 'Silakan masuk dengan password baru Anda.',
      });
      navigate(LOGIN_ROUTE, { replace: true });
    } catch (err) {
      const { type } = apiErrorBody(err);
      const message = apiErrorMessage(err, 'Gagal menyimpan password.');
      if (type === 'password' || type === 'confirm_password') {
        setErrors({ [type]: message });
      } else if (type === 'otp') {
        // Verified too long ago: start over from the phone number
        usePendingAuthStore.getState().clear();
        showToast({ type: 'error', title: 'Gagal menyimpan', message });
        navigate(FORGOT_PASSWORD_ROUTE, { replace: true });
      } else {
        showToast({ type: 'error', title: 'Gagal menyimpan', message });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="h-screen w-full overflow-hidden justify-between pb-[50px] bg-[linear-gradient(180deg,#FFF_0%,#007AFF_250.56%)]">
      <HeaderMobile className="bg-transparent!">
        <TopBarHeader
          type="back"
          title="Password Baru"
          onBack={() => navigate(LOGIN_ROUTE, { replace: true })}
        />
      </HeaderMobile>

      <WrapperMobile className="flex-1 overflow-auto m-auto h-full">
        <form
          onSubmit={onSubmit}
          noValidate
          className="shadowCard h-fit p-6 flex-col flex gap-6 rounded-[24px] mt-[90px] sm:m-auto md:m-auto lg:m-auto"
        >
          <div className="flex flex-col items-center gap-2 text-center">
            <Lock className="size-8 text-black" aria-hidden="true" />
            <p className="title2medium text-black">Buat password baru</p>
            <p className="reg12 text-text-grey">Buat password baru untuk mengamankan akun anda.</p>
          </div>

          <div className="flex flex-col gap-3">
            <PasswordMobile
              label="Password Baru"
              name="password"
              placeholder="Masukkan password baru"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={errors.password}
            />
            <PasswordMobile
              label="Konfirmasi Password Baru"
              name="confirmPassword"
              placeholder="Masukkan kembali password baru"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={errors.confirm_password}
            />
          </div>

          <Button
            type="submit"
            customButton="btn-fill-blue"
            disabled={!password || !confirmPassword || isSaving}
          >
            <span className="title2medium text-white">{isSaving ? 'Menyimpan...' : 'Simpan'}</span>
          </Button>
        </form>
      </WrapperMobile>
    </main>
  );
};

export default NewPassword;
